/**
 * Gap Distribution Chart Component for Personal Gap Analysis
 * Displays an interactive SVG pie chart showing question distribution across gap zones
 */
import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';

const GapDistributionChart = ({
  answeredQuestions,
  personalAssessment,
  hoveredGapZone,
  setHoveredGapZone,
  selectedGapFilter,
  setSelectedGapFilter,
  setExpandedCategory,
  getQuestionsByGapZone,
  getGapZoneInfo,
  handleGapZoneClick,
  getCategoryQuestions,
  finalCategoryKeys
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-6">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-bold text-gray-900">
          Gap Distribution
        </h3>
        {selectedGapFilter && (
          <button
            onClick={() => {
              setSelectedGapFilter(null);
              setExpandedCategory([]);
            }}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition"
          >
            <X size={16} />
            <span>Reset Filter</span>
          </button>
        )}
      </div>
      <div className="relative">
        {answeredQuestions.length > 0 ? (
          <div className="relative w-full h-48 sm:h-64 flex items-center justify-center">
            <svg viewBox="0 0 200 200" className="w-48 sm:w-64 h-48 sm:h-64">
              {(() => {
                const questionsByZone = getQuestionsByGapZone();

                // Filter out N/A questions (answer_value === 0) for chart calculation
                const totalNonNA = answeredQuestions.filter(q => q.answer_value !== 0).length;

                let currentAngle = -90;

                return Object.keys(questionsByZone)
                  .filter(zone => zone !== 'n-a') // Exclude N/A zone from chart
                  .map((zone) => {
                  // Get count for this zone (already excludes N/A since we filtered the zone)
                  const count = questionsByZone[zone].length;

                  if (count === 0) return null;

                  const zoneInfo = getGapZoneInfo(zone);
                  const sweepAngle = (count / totalNonNA) * 360;

                  const startAngle = currentAngle;
                  const endAngle = currentAngle + sweepAngle;
                  const isHovered = hoveredGapZone === zone;
                  const isSelected = selectedGapFilter === zone;

                  // Base segment (always visible, darker)
                  const baseOuterRadius = 80;
                  const baseInnerRadius = 50;

                  // Expanded segment (on hover, lighter and transparent)
                  const expandedOuterRadius = 90;
                  const expandedInnerRadius = 45;

                  const largeArc = sweepAngle > 180 ? 1 : 0;

                  currentAngle = endAngle;

                  return (
                    <g key={zone}>
                      {/* Base lighter segment */}
                      <path
                        d={`
                          M ${100 + baseInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                          L ${100 + baseOuterRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseOuterRadius * Math.sin((startAngle * Math.PI) / 180)}
                          A ${baseOuterRadius} ${baseOuterRadius} 0 ${largeArc} 1 ${100 + baseOuterRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + baseOuterRadius * Math.sin((endAngle * Math.PI) / 180)}
                          L ${100 + baseInnerRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((endAngle * Math.PI) / 180)}
                          A ${baseInnerRadius} ${baseInnerRadius} 0 ${largeArc} 0 ${100 + baseInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                          Z
                        `}
                        fill={zoneInfo.color}
                        fillOpacity={1}
                        className="transition-all duration-200"
                        style={{
                          filter: 'brightness(1.1)'
                        }}
                      />

                      {/* Expanded darker segment on hover or selected (with transparency) */}
                      {(isHovered || isSelected) && (
                        <path
                          d={`
                            M ${100 + expandedInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + expandedInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                            L ${100 + expandedOuterRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + expandedOuterRadius * Math.sin((startAngle * Math.PI) / 180)}
                            A ${expandedOuterRadius} ${expandedOuterRadius} 0 ${largeArc} 1 ${100 + expandedOuterRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + expandedOuterRadius * Math.sin((endAngle * Math.PI) / 180)}
                            L ${100 + expandedInnerRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + expandedInnerRadius * Math.sin((endAngle * Math.PI) / 180)}
                            A ${expandedInnerRadius} ${expandedInnerRadius} 0 ${largeArc} 0 ${100 + expandedInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + expandedInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                            Z
                          `}
                          fill={zoneInfo.color}
                          fillOpacity={0.4}
                          className="transition-all duration-200"
                          style={{
                            filter: 'brightness(0.7)'
                          }}
                        />
                      )}

                      {/* Invisible clickable overlay */}
                      <path
                        d={`
                          M ${100 + expandedInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + expandedInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                          L ${100 + expandedOuterRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + expandedOuterRadius * Math.sin((startAngle * Math.PI) / 180)}
                          A ${expandedOuterRadius} ${expandedOuterRadius} 0 ${largeArc} 1 ${100 + expandedOuterRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + expandedOuterRadius * Math.sin((endAngle * Math.PI) / 180)}
                          L ${100 + expandedInnerRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + expandedInnerRadius * Math.sin((endAngle * Math.PI) / 180)}
                          A ${expandedInnerRadius} ${expandedInnerRadius} 0 ${largeArc} 0 ${100 + expandedInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + expandedInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                          Z
                        `}
                        fill="transparent"
                        onMouseEnter={() => setHoveredGapZone(zone)}
                        onMouseLeave={() => setHoveredGapZone(null)}
                        onClick={() => handleGapZoneClick(zone)}
                        className="cursor-pointer"
                      />
                    </g>
                  );
                });
              })()}

              <circle cx="100" cy="100" r="50" fill="white" />
            </svg>

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                {hoveredGapZone ? (
                  <>
                    <div className="text-2xl sm:text-3xl font-bold" style={{ color: getGapZoneInfo(hoveredGapZone).color, filter: 'brightness(0.6)' }}>
                      {getQuestionsByGapZone()[hoveredGapZone].length}
                    </div>
                    <div className="text-xs font-semibold text-gray-800 mt-1">
                      {getGapZoneInfo(hoveredGapZone).label}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-3xl sm:text-4xl font-bold text-gray-900">
                      {Math.round(personalAssessment.overall_score)}
                    </div>
                    <div className="text-xs font-semibold text-gray-700">Score</div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-2">
              <svg className="w-24 h-24 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-gray-600">No data yet</p>
            <p className="text-sm text-gray-500">Answer questions to see distribution</p>
          </div>
        )}
      </div>

      {personalAssessment.answered_questions > 0 && (
        <div className="mt-4 sm:mt-6 space-y-2">
          {(() => {
            const questionsByZone = getQuestionsByGapZone();
            const zones = ['no-gaps', 'minor-gaps', 'considerable-gaps', 'critical-gaps', 'very-critical-gaps', 'extremely-critical'];

            // Count N/A questions separately
            const naCount = answeredQuestions.filter(q => q.answer_value === 0).length;

            return (
              <>
                {zones.map((zone) => {
                  // Get count for this zone
                  const count = questionsByZone[zone].length;
                  if (count === 0) return null;

                  const zoneInfo = getGapZoneInfo(zone);
                  const isHovered = hoveredGapZone === zone;
                  const isSelected = selectedGapFilter === zone;

                  return (
                    <div
                      key={zone}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition ${
                        isHovered || isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                      }`}
                      onMouseEnter={() => setHoveredGapZone(zone)}
                      onMouseLeave={() => setHoveredGapZone(null)}
                      onClick={() => handleGapZoneClick(zone)}
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: zoneInfo.color }}
                        ></div>
                        <span className="text-sm text-gray-700">
                          {zoneInfo.label} ({zoneInfo.range})
                        </span>
                      </div>
                      <span className={`text-sm font-semibold ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>
                        {count}
                      </span>
                    </div>
                  );
                })}

                {/* N/A Questions Section - Clickable */}
                {naCount > 0 && (
                  <div
                    className={`flex items-center justify-between p-2 rounded cursor-pointer transition border-t border-gray-200 mt-2 ${
                      selectedGapFilter === 'n-a' ? 'bg-gray-100' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      const newFilter = selectedGapFilter === 'n-a' ? null : 'n-a';
                      setSelectedGapFilter(newFilter);

                      // Auto-expand categories with N/A questions
                      if (newFilter === 'n-a') {
                        const categoriesToExpand = [];

                        finalCategoryKeys.forEach(catKey => {
                          const categoryQuestions = getCategoryQuestions(catKey);
                          const hasNAQuestions = categoryQuestions.some(q => {
                            const answer = answeredQuestions.find(a => a.question_id === q.question_id);
                            return answer && answer.answer_value === 0;
                          });

                          if (hasNAQuestions) {
                            categoriesToExpand.push(catKey);
                          }
                        });

                        setExpandedCategory(categoriesToExpand.length > 0 ? categoriesToExpand : null);
                      } else {
                        setExpandedCategory(null);
                      }
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded bg-gray-400"></div>
                      <span className="text-sm text-gray-600">
                        N/A - Not Applicable
                      </span>
                    </div>
                    <span className={`text-sm font-semibold ${selectedGapFilter === 'n-a' ? 'text-blue-600' : 'text-gray-600'}`}>
                      {naCount}
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {selectedGapFilter && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center justify-between">
          <span>Showing "{selectedGapFilter === 'n-a' ? 'N/A - Not Applicable' : getGapZoneInfo(selectedGapFilter).label}" questions</span>
          <button
            onClick={() => {
              setSelectedGapFilter(null);
              setExpandedCategory([]);
            }}
            className="text-blue-600 hover:text-blue-800 transition"
            title="Clear filter"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

GapDistributionChart.propTypes = {
  answeredQuestions: PropTypes.array.isRequired,
  personalAssessment: PropTypes.shape({
    overall_score: PropTypes.number,
    answered_questions: PropTypes.number
  }).isRequired,
  hoveredGapZone: PropTypes.string,
  setHoveredGapZone: PropTypes.func.isRequired,
  selectedGapFilter: PropTypes.string,
  setSelectedGapFilter: PropTypes.func.isRequired,
  setExpandedCategory: PropTypes.func.isRequired,
  getQuestionsByGapZone: PropTypes.func.isRequired,
  getGapZoneInfo: PropTypes.func.isRequired,
  handleGapZoneClick: PropTypes.func.isRequired,
  getCategoryQuestions: PropTypes.func.isRequired,
  finalCategoryKeys: PropTypes.array.isRequired
};

export default GapDistributionChart;
